import { DocumentServicesObject } from '@wix/document-services-types';

import {
  CLASSIC_NAMESPACES,
  COMMON_NAMESPACES,
  EDITORX_NAMESPACES,
} from './createPrivateAPI';
import {
  HostAPIClassic,
  HostAPIEditorX,
  HostAPIBase,
  HostAPIStudio,
} from './HostAPI';
import type { WithPromiseFunctions } from '@wix/editor-platform-transport';

export type AnyFunction = (...args: any[]) => any;

export enum HostType {
  Classic = 'editor',
  Editorx = 'editor_x',
  Studio = 'studio',
  Editor3 = 'editor3',
  MobileStudio = 'mobile_app_builder',
  Blocks = 'blocks',
  Studio2 = 'studio2',
}

export interface DocumentServices_unsafe {
  runInContext<F extends AnyFunction>(context: string, fn: F): ReturnType<F>;
}

export type DocumentServices = DocumentServices_unsafe & DocumentServicesObject;

type OptionalRootProperties<T> = {
  [K in keyof T]?: T[K];
};

type Promisify<T> = {
  [K in keyof T]: T[K] extends (...args: infer Args) => infer R
    ? (...args: Args) => Promise<R>
    : T[K];
};
export type HostAPI = OptionalRootProperties<HostAPIClassic> &
  OptionalRootProperties<HostAPIEditorX> &
  OptionalRootProperties<HostAPIStudio> &
  HostAPIBase;

type IPrivateAPICommon = {
  [K in (typeof COMMON_NAMESPACES)[number]['namespace']]: InstanceType<
    Extract<(typeof COMMON_NAMESPACES)[number], { namespace: K }>
  >;
};

type IPrivateAPIClassic = {
  [K in (typeof CLASSIC_NAMESPACES)[number]['namespace']]: InstanceType<
    Extract<(typeof CLASSIC_NAMESPACES)[number], { namespace: K }>
  >;
};

type IPrivateAPIEditorX = {
  [K in (typeof EDITORX_NAMESPACES)[number]['namespace']]: InstanceType<
    Extract<(typeof EDITORX_NAMESPACES)[number], { namespace: K }>
  >;
};

export type IPrivateAPI = OptionalRootProperties<IPrivateAPIClassic> &
  OptionalRootProperties<IPrivateAPIEditorX> &
  IPrivateAPICommon;

export type WrappedPrivateAPI = Promisify<IPrivateAPI>;

// TODO: to be linked to the class from application package when we will have a types sharing approach
// TODO: generics should be on class level instead of function  level
export type IEditorPlatformApplication_unsafe<
  TPublicAPI = unknown,
  TPrivateAPI = unknown,
> = WithPromiseFunctions<{
  appDefinitionId: string;
  getPublicAPI: () => TPublicAPI;
  getPrivateAPI: () => TPrivateAPI;
}>;

/**
 * TODO: experiments adapter should be implemented on our side,
 * and exposed for all EP packages
 * currently we just use types (and copy-pasted here) from
 *
 * import { ExperimentsAPI } from '@wix/editorx-core-api';
 */
export interface IExperimentsAPIFixMe {
  enabled: (key: string) => boolean;
  getValue: (key: string) => string | undefined;
  all: () => {
    [key: string]: string | boolean;
  };
}

type BaseEditorType = HostAPI['editor'];

export type EditorMethodArgs<T extends keyof BaseEditorType> =
  BaseEditorType[T] extends (...args: any) => void
    ? Parameters<BaseEditorType[T]>
    : [];
